import React from 'react'
import { Building2 } from 'lucide-react'
import { cn } from '@/lib/utils'

/**
 * The one identity glyph every market section uses: a numbered slate disc per competitor (compset
 * order, 1 to n) and a teal "You". The same number names the same hotel in the chart key, the
 * night's lineup, the moves list, the tables and the price-and-reviews map.
 *
 * `isBuilding` marks another listing of this building on the Compset page: a dashed ring with no
 * number, because it is not a competitor and holds no place in the compset order.
 */
export default function CompetitorBadge({
  number,
  isYou = false,
  isBuilding = false,
  size = 'sm',
  className,
}: {
  number?: number | null
  isYou?: boolean
  isBuilding?: boolean
  size?: 'sm' | 'md'
  className?: string
}) {
  if (isBuilding) {
    return (
      <span
        aria-hidden="true"
        className={cn(
          'inline-flex shrink-0 items-center justify-center rounded-full border border-dashed border-slate-400 bg-slate-50 text-slate-500',
          size === 'sm' ? 'h-4 w-4' : 'h-5 w-5',
          className
        )}
      >
        <Building2 className={size === 'sm' ? 'h-2.5 w-2.5' : 'h-3 w-3'} />
      </span>
    )
  }
  if (isYou) {
    return (
      <span
        className={cn(
          'inline-flex shrink-0 items-center justify-center rounded-full bg-primary font-semibold text-white',
          size === 'sm'
            ? 'h-4 px-1.5 text-[9px] uppercase tracking-wide'
            : 'h-5 px-2 text-[10px] uppercase tracking-wide',
          className
        )}
      >
        You
      </span>
    )
  }
  return (
    <span
      aria-hidden="true"
      className={cn(
        'inline-flex shrink-0 items-center justify-center rounded-full bg-slate-600 font-semibold tabular-nums text-white',
        size === 'sm' ? 'h-4 w-4 text-[10px]' : 'h-5 w-5 text-[11px]',
        className
      )}
    >
      {number ?? ''}
    </span>
  )
}
